import { useState, useCallback, useMemo } from 'react';

export const useAnalyticsData = (searchResults, searchParams) => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Table sorting state
  const [wellGroupingTableSort, setWellGroupingTableSort] = useState({ field: '', direction: 'asc' });
  const [riskWellsTableSort, setRiskWellsTableSort] = useState({ field: 'inactive_years', direction: 'desc' });
  const [multipleRecordsTableSort, setMultipleRecordsTableSort] = useState({ field: 'api_number', direction: 'asc' });
  const [productionTableSort, setProductionTableSort] = useState({ field: 'month', direction: 'desc' });
  const [inspectionsTableSort, setInspectionsTableSort] = useState({ field: 'inspection_date', direction: 'desc' });
  const [violationsTableSort, setViolationsTableSort] = useState({ field: 'violation_discovery_date', direction: 'desc' });
  const [violationsByRuleTableSort, setViolationsByRuleTableSort] = useState({ field: 'violations', direction: 'desc' });
  const [severancesTableSort, setSeverancesTableSort] = useState({ field: 'certified_letter_date', direction: 'desc' });
  const [spillsTableSort, setSpillsTableSort] = useState({ field: 'date_of_loss', direction: 'desc' });

  // Toggle filter state
  const [riskWellsFilter, setRiskWellsFilter] = useState('over_5_years'); // 'over_5_years' or 'all_inactive'
  const [violationsFilter, setViolationsFilter] = useState('unresolved'); // 'all', 'unresolved', or 'major'
  const [severancesFilter, setSeverancesFilter] = useState('unresolved'); // 'all' or 'unresolved'
  const [inspectionsFilter, setInspectionsFilter] = useState('not_compliant'); // 'all' or 'not_compliant'
  const [inactiveYearsBinSize, setInactiveYearsBinSize] = useState('5-year'); // '5-year' or '1-year'

  // Chart visibility state
  const [hiddenWellGroups, setHiddenWellGroups] = useState(new Set());
  const [hiddenViolationsLines, setHiddenViolationsLines] = useState(new Set());
  const [hiddenReinspectionLines, setHiddenReinspectionLines] = useState(new Set());
  const [hiddenSeverancesLines, setHiddenSeverancesLines] = useState(new Set());
  const [hiddenSeveranceVolumeLines, setHiddenSeveranceVolumeLines] = useState(new Set());
  const [hiddenInspectionLines, setHiddenInspectionLines] = useState(new Set());
  const [hiddenProductionLines, setHiddenProductionLines] = useState(new Set());
  const [hiddenFlaringLines, setHiddenFlaringLines] = useState(new Set());
  const [hiddenFuelGasLines, setHiddenFuelGasLines] = useState(new Set());
  const [hiddenSpillLines, setHiddenSpillLines] = useState(new Set());
  const [hiddenSpillVolumeLines, setHiddenSpillVolumeLines] = useState(new Set());

  // Get available table types based on actual search results data
  const availableTableTypes = useMemo(() => {
    if (!searchResults?.data) return [];
    
    const tableTypes = ['wells', 'production', 'inspections', 'violations', 'severances', 'spills'];
    return tableTypes.filter(tableType => {
      const data = searchResults.data[tableType];
      return data && Array.isArray(data) && data.length > 0;
    });
  }, [searchResults]);

  // Get current table type based on active tab
  const getCurrentTableType = useCallback(() => {
    if (availableTableTypes.length === 0) return null;
    
    // Check if this is the Matches tab (index 0) or Summary tab (index 1)
    if (activeTab === 0) {
      return 'matches'; // Special case for Matches tab
    }
    if (activeTab === 1) {
      return 'relational'; // Special case for Summary tab
    }
    
    // If current tab is out of bounds, reset to first available tab
    if (activeTab >= availableTableTypes.length + 2) {
      setActiveTab(0);
      return 'matches';
    }
    
    // For other tabs, subtract 2 to account for Matches (0) and Summary (1) tabs
    return availableTableTypes[activeTab - 2];
  }, [availableTableTypes, activeTab]);

  // Get current table data
  const getCurrentTableData = useCallback(() => {
    const tableType = getCurrentTableType();
    if (!searchResults?.data) return [];
    return searchResults.data[tableType] || [];
  }, [searchResults, getCurrentTableType]);

  // Get current table counts
  const getCurrentTableCounts = useCallback(() => {
    const tableType = getCurrentTableType();
    if (!searchResults?.counts) return 0;
    return searchResults.counts[tableType] || 0;
  }, [searchResults, getCurrentTableType]);

  // Generic table sort handler
  const createTableSortHandler = useCallback((setSortState) => {
    return (field) => {
      setSortState(prev => ({
        field,
        direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    };
  }, []);

  // Specific table sort handlers
  const handleWellGroupingTableSort = useMemo(() => 
    createTableSortHandler(setWellGroupingTableSort), [createTableSortHandler]);
  const handleRiskWellsTableSort = useMemo(() => 
    createTableSortHandler(setRiskWellsTableSort), [createTableSortHandler]);
  const handleMultipleRecordsTableSort = useMemo(() => 
    createTableSortHandler(setMultipleRecordsTableSort), [createTableSortHandler]);
  const handleProductionTableSort = useMemo(() => 
    createTableSortHandler(setProductionTableSort), [createTableSortHandler]);
  const handleInspectionsTableSort = useMemo(() => 
    createTableSortHandler(setInspectionsTableSort), [createTableSortHandler]);
  const handleViolationsTableSort = useMemo(() => 
    createTableSortHandler(setViolationsTableSort), [createTableSortHandler]);
  const handleViolationsByRuleTableSort = useMemo(() => 
    createTableSortHandler(setViolationsByRuleTableSort), [createTableSortHandler]);
  const handleSeverancesTableSort = useMemo(() => 
    createTableSortHandler(setSeverancesTableSort), [createTableSortHandler]);
  const handleSpillsTableSort = useMemo(() => 
    createTableSortHandler(setSpillsTableSort), [createTableSortHandler]);

  // Reset functions for tables
  const resetMultipleRecordsTableSort = useCallback(() => {
    setMultipleRecordsTableSort({ field: 'api_number', direction: 'asc' });
  }, []);

  const resetViolationsByRuleTableSort = useCallback(() => {
    setViolationsByRuleTableSort({ field: 'violations', direction: 'desc' });
  }, []);

  // Reset functions for toggle filters
  const resetRiskWellsFilter = useCallback(() => {
    setRiskWellsFilter('over_5_years');
  }, []);

  const resetViolationsFilter = useCallback(() => {
    setViolationsFilter('unresolved');
  }, []);

  const resetSeverancesFilter = useCallback(() => {
    setSeverancesFilter('unresolved');
  }, []);

  const resetInspectionsFilter = useCallback(() => {
    setInspectionsFilter('not_compliant');
  }, []);

  const resetInactiveYearsBinSize = useCallback(() => {
    setInactiveYearsBinSize('5-year');
  }, []);

  // Reset functions for chart visibility
  const resetHiddenWellGroups = useCallback(() => {
    setHiddenWellGroups(new Set());
  }, []);

  const resetHiddenViolationsLines = useCallback(() => {
    setHiddenViolationsLines(new Set());
  }, []);

  const resetHiddenReinspectionLines = useCallback(() => {
    setHiddenReinspectionLines(new Set());
  }, []);

  const resetHiddenSeverancesLines = useCallback(() => {
    setHiddenSeverancesLines(new Set());
  }, []);

  const resetHiddenSeveranceVolumeLines = useCallback(() => {
    setHiddenSeveranceVolumeLines(new Set());
  }, []);

  const resetHiddenInspectionLines = useCallback(() => {
    setHiddenInspectionLines(new Set());
  }, []);

  const resetHiddenProductionLines = useCallback(() => {
    setHiddenProductionLines(new Set());
  }, []);

  const resetHiddenFlaringLines = useCallback(() => {
    setHiddenFlaringLines(new Set());
  }, []);

  const resetHiddenFuelGasLines = useCallback(() => {
    setHiddenFuelGasLines(new Set());
  }, []);

  const resetHiddenSpillLines = useCallback(() => {
    setHiddenSpillLines(new Set());
  }, []);

  const resetHiddenSpillVolumeLines = useCallback(() => {
    setHiddenSpillVolumeLines(new Set());
  }, []);

  const resetProductionTableSort = useCallback(() => {
    setProductionTableSort({ field: 'month', direction: 'desc' });
  }, []);

  const resetInspectionsTableSort = useCallback(() => {
    setInspectionsTableSort({ field: 'inspection_date', direction: 'desc' });
  }, []);

  const resetViolationsTableSort = useCallback(() => {
    setViolationsTableSort({ field: 'violation_discovery_date', direction: 'desc' });
  }, []);

  const resetSeverancesTableSort = useCallback(() => {
    setSeverancesTableSort({ field: 'certified_letter_date', direction: 'desc' });
  }, []);

  const resetSpillsTableSort = useCallback(() => {
    setSpillsTableSort({ field: 'date_of_loss', direction: 'desc' });
  }, []);

  // Get unique operator names for a specific table type
  const getUniqueOperatorNames = useCallback((tableType) => {
    if (!searchResults?.data || !searchResults.data[tableType]) return [];
    
    const data = searchResults.data[tableType];
    const operatorNames = new Set();
    
    data.forEach(record => {
      const operator = record.operator_name || record['Operator Name'] || record.operator;
      if (operator && operator.toString().trim() !== '') {
        operatorNames.add(operator.toString().trim());
      }
    });
    
    return Array.from(operatorNames).sort();
  }, [searchResults]);

  // Get all unique operator names across all tables
  const getAllUniqueOperatorNames = useCallback(() => {
    if (!searchResults?.data) return [];
    
    const allOperatorNames = new Set();
    Object.values(searchResults.data).forEach(tableData => {
      tableData.forEach(record => {
        const operator = record.operator_name || record['Operator Name'] || record.operator;
        if (operator && operator.toString().trim() !== '') {
          allOperatorNames.add(operator.toString().trim());
        }
      });
    });
    
    return Array.from(allOperatorNames).sort();
  }, [searchResults]);

  // Calculate analytics metrics for a specific table type
  const getTableAnalytics = useCallback((tableType) => {
    if (!searchResults?.data || !searchResults.data[tableType]) {
      return {
        totalRecords: 0,
        uniqueOperators: 0,
        dateRange: null,
        complianceRate: null,
        totalValue: null,
        averageValue: null,
      };
    }

    const data = searchResults.data[tableType];
    const totalRecords = data.length;
    const uniqueOperators = getUniqueOperatorNames(tableType).length;

    // Calculate date range
    let dateRange = null;
    const dateColumns = ['date', 'Date', 'created_date', 'Created Date', 'inspection_date', 'Inspection Date'];
    const dateColumn = dateColumns.find(col => data[0] && data[0][col] !== undefined);
    
    if (dateColumn) {
      const dates = data
        .map(record => record[dateColumn])
        .filter(date => date && date.toString().trim() !== '')
        .map(date => new Date(date))
        .filter(date => !isNaN(date.getTime()));
      
      if (dates.length > 0) {
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        dateRange = {
          start: minDate,
          end: maxDate,
          formatted: `${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`
        };
      }
    }

    // Calculate compliance rate (for inspections/violations)
    let complianceRate = null;
    if (tableType === 'inspections' || tableType === 'violations') {
      const complianceColumn = data[0]['Compliance'] !== undefined ? 'Compliance' :
                            data[0].compliance !== undefined ? 'compliance' : null;
      
      if (complianceColumn) {
        const compliantRecords = data.filter(record => {
          const compliance = record[complianceColumn];
          return compliance && compliance.toString().toLowerCase().includes('compliant');
        });
        complianceRate = totalRecords > 0 ? (compliantRecords.length / totalRecords) * 100 : 0;
      }
    }

    // Calculate total and average values (for production/spills)
    let totalValue = null;
    let averageValue = null;
    if (tableType === 'production') {
      const oilTotal = data.reduce((sum, record) => sum + (parseFloat(record.oil_bbl) || 0), 0);
      const gasTotal = data.reduce((sum, record) => sum + (parseFloat(record.gas_mcf) || 0), 0);
      totalValue = { oil: oilTotal, gas: gasTotal };
      averageValue = totalRecords > 0 ? { oil: oilTotal / totalRecords, gas: gasTotal / totalRecords } : null;
    } else if (tableType === 'spills') {
      const volumeColumn = data[0]['Volume Lost'] !== undefined ? 'Volume Lost' :
                         data[0].volume_lost !== undefined ? 'volume_lost' : null;
      
      if (volumeColumn) {
        const totalVolume = data.reduce((sum, record) => sum + (parseFloat(record[volumeColumn]) || 0), 0);
        totalValue = { volume: totalVolume };
        averageValue = totalRecords > 0 ? { volume: totalVolume / totalRecords } : null;
      }
    }

    return {
      totalRecords,
      uniqueOperators,
      dateRange,
      complianceRate,
      totalValue,
      averageValue,
    };
  }, [searchResults, getUniqueOperatorNames]);

  // Get all analytics metrics
  const getAllAnalytics = useCallback(() => {
    const analytics = {};
    availableTableTypes.forEach(tableType => {
      analytics[tableType] = getTableAnalytics(tableType);
    });
    return analytics;
  }, [availableTableTypes, getTableAnalytics]);

  // Reset all sorting and tab state
  const resetAllSorting = useCallback(() => {
    setActiveTab(0);
    setWellGroupingTableSort({ field: '', direction: 'asc' });
    setRiskWellsTableSort({ field: 'inactive_years', direction: 'desc' });
    setProductionTableSort({ field: 'month', direction: 'desc' });
    setInspectionsTableSort({ field: 'inspection_date', direction: 'desc' });
    setViolationsTableSort({ field: 'violation_discovery_date', direction: 'desc' });
    setSeverancesTableSort({ field: 'certified_letter_date', direction: 'desc' });
    setSpillsTableSort({ field: 'date_of_loss', direction: 'desc' });
  }, []);

  // Reset specific table sorting
  const resetTableSorting = useCallback((tableType) => {
    switch (tableType) {
      case 'wells':
        setWellGroupingTableSort({ field: '', direction: 'asc' });
        setRiskWellsTableSort({ field: 'inactive_years', direction: 'desc' });
        break;
      case 'production':
        setProductionTableSort({ field: '', direction: 'asc' });
        break;
      case 'inspections':
        setInspectionsTableSort({ field: '', direction: 'asc' });
        break;
      case 'violations':
        setViolationsTableSort({ field: '', direction: 'asc' });
        break;
      case 'severances':
        setSeverancesTableSort({ field: '', direction: 'asc' });
        break;
      case 'spills':
        setSpillsTableSort({ field: 'date_of_loss', direction: 'desc' });
        break;
      default:
        break;
    }
  }, []);

  return {
    // State
    activeTab,
    wellGroupingTableSort,
    riskWellsTableSort,
    multipleRecordsTableSort,
    productionTableSort,
    inspectionsTableSort,
    violationsTableSort,
    violationsByRuleTableSort,
    severancesTableSort,
    spillsTableSort,
    riskWellsFilter,
    violationsFilter,
    severancesFilter,
    inspectionsFilter,
    inactiveYearsBinSize,
    hiddenWellGroups,
    hiddenViolationsLines,
    hiddenReinspectionLines,
    hiddenSeverancesLines,
    hiddenSeveranceVolumeLines,
    hiddenInspectionLines,
    hiddenProductionLines,
    hiddenFlaringLines,
    hiddenFuelGasLines,
    hiddenSpillLines,
    hiddenSpillVolumeLines,
    
    // Setters
    setActiveTab,
    setWellGroupingTableSort,
    setRiskWellsTableSort,
    setMultipleRecordsTableSort,
    setProductionTableSort,
    setInspectionsTableSort,
    setViolationsTableSort,
    setViolationsByRuleTableSort,
    setSeverancesTableSort,
    setSpillsTableSort,
    setRiskWellsFilter,
    setViolationsFilter,
    setSeverancesFilter,
    setInspectionsFilter,
    setInactiveYearsBinSize,
    setHiddenWellGroups,
    setHiddenViolationsLines,
    setHiddenReinspectionLines,
    setHiddenSeverancesLines,
    setHiddenSeveranceVolumeLines,
    setHiddenInspectionLines,
    setHiddenProductionLines,
    setHiddenFlaringLines,
    setHiddenFuelGasLines,
    setHiddenSpillLines,
    setHiddenSpillVolumeLines,
    
    // Computed values
    availableTableTypes,
    getCurrentTableType,
    getCurrentTableData,
    getCurrentTableCounts,
    
    // Functions
    handleWellGroupingTableSort,
    handleRiskWellsTableSort,
    handleMultipleRecordsTableSort,
    handleProductionTableSort,
    handleInspectionsTableSort,
    handleViolationsTableSort,
    handleViolationsByRuleTableSort,
    handleSeverancesTableSort,
    handleSpillsTableSort,
    resetMultipleRecordsTableSort,
    resetViolationsByRuleTableSort,
    resetRiskWellsFilter,
    resetViolationsFilter,
    resetSeverancesFilter,
    resetInspectionsFilter,
    resetInactiveYearsBinSize,
    resetHiddenWellGroups,
    resetHiddenViolationsLines,
    resetHiddenReinspectionLines,
    resetHiddenSeverancesLines,
    resetHiddenSeveranceVolumeLines,
    resetHiddenInspectionLines,
    resetHiddenProductionLines,
    resetHiddenFlaringLines,
    resetHiddenFuelGasLines,
    resetHiddenSpillLines,
    resetHiddenSpillVolumeLines,
    resetProductionTableSort,
    resetInspectionsTableSort,
    resetViolationsTableSort,
    resetSeverancesTableSort,
    resetSpillsTableSort,
    getUniqueOperatorNames,
    getAllUniqueOperatorNames,
    getTableAnalytics,
    getAllAnalytics,
    resetAllSorting,
    resetTableSorting,
  };
};

export default useAnalyticsData;
